import { Search } from 'lucide-react';
import { useState, type RefObject } from 'react';

import { Popover } from '~/components/ui';
import {
  DYNAMIC_FIELD_GROUPS,
  filterDynamicFieldGroups,
  type DynamicField,
} from '~/lib/ai/dynamicFields';

interface DynamicFieldMenuProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  /** Shown under the search box when an insert would pass the limit; a new `key` is announced again. */
  notice?: { key: number; text: string };
  onSearchChange?: () => void;
  onSelect: (field: DynamicField) => void;
  testId: string;
}

function DynamicFieldList({
  notice,
  onSearchChange,
  onSelect,
  testId,
}: Omit<DynamicFieldMenuProps, 'anchorRef' | 'open' | 'onClose'>) {
  const [search, setSearch] = useState('');
  const groups = filterDynamicFieldGroups(DYNAMIC_FIELD_GROUPS, search);

  return (
    <>
      <div className="border-b border-gray-200 p-3 dark:border-gray-800">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
          />
          <input
            autoFocus
            aria-label="Search dynamic fields"
            className="h-9 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            data-testid={`${testId}-search`}
            onChange={(event) => {
              setSearch(event.target.value);
              onSearchChange?.();
            }}
            onKeyDown={(event) => {
              const firstMatch = groups[0]?.fields[0];

              if (event.key === 'Enter' && firstMatch) {
                event.preventDefault();
                onSelect(firstMatch);
              }
            }}
            placeholder="Search"
            type="text"
            value={search}
          />
        </div>
        {notice ? (
          <p
            key={notice.key}
            role="alert"
            className="mt-2 text-xs text-error-600 dark:text-error-400"
          >
            {notice.text}
          </p>
        ) : null}
      </div>

      <div className="max-h-64 overflow-y-auto py-2">
        {groups.length === 0 ? (
          <p className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">No matching fields</p>
        ) : (
          groups.map((group) => (
            <div key={group.title} className="pb-2 last:pb-0">
              <p className="px-4 py-1 text-xs font-semibold uppercase text-gray-400">
                {group.title}
              </p>
              {group.fields.map((field) => (
                <button
                  key={field.token}
                  type="button"
                  data-testid={`${testId}-${field.token}`}
                  className="focus-ring block w-full px-4 py-1.5 text-left text-sm text-gray-800 hover:bg-gray-100 dark:text-white/90 dark:hover:bg-white/5"
                  onClick={() => onSelect(field)}
                >
                  {field.label}
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );
}

/** The `{}` menu: a searchable, grouped list of merge fields; Enter picks the first match. */
export function DynamicFieldMenu({ anchorRef, open, onClose, ...list }: DynamicFieldMenuProps) {
  return (
    <Popover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement="bottom-end"
      ariaLabel="Dynamic fields"
      testId={list.testId}
      className="w-64 overflow-hidden"
    >
      <DynamicFieldList {...list} />
    </Popover>
  );
}
